import React, { useState, useCallback, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import MuiAlert from '@mui/material/Alert';
import ButtonGroup from '@mui/material/ButtonGroup';
import WarningIcon from '@mui/icons-material/Warning';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useTheme } from '@mui/material/styles';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:17001';

// Types
interface WorkOrder {
  id: string;
  name: string;
  location: string;
  image: string;
  repeatableCount: number;
  status: string;
  priority: string;
  assignedTo: string;
  estimatedHours: number;
  actualHours: number;
  startDate: string;
  endDate: string;
  workOrders?: Array<{
    id: string;
    description: string;
    type: string;
    status: string;
    date: string;
  }>;
}

interface ScenarioData {
  strategy: string;
  crewSize: number;
  fixedEvents: number;
  totalCost: number;
  totalDowntime: number;
  efficiency: number;
  breakdown: Array<{
    category: string;
    value: number;
  }>;
  failureCurves?: Array<Array<{ day: number; value: number }>>;
  summaryData?: Array<{
    scenario: string;
    strategy: string;
    maxCrew: number;
    production: number;
    cost: number;
  }>;
  scheduleData?: Array<{
    machine: string;
    start: number;
    end: number;
    type: string;
  }>;
  productionData?: Array<{
    day: number;
    [key: string]: number;
  }>;
  crewUsageData?: Array<{
    day: number;
    people: number;
  }>;
  costData?: Array<{
    label: string;
    value: number;
    bold?: boolean;
  }>;
}

interface MaintenanceProps {
  year?: string;
  month?: string;
  line?: string;
  station?: string;
  title?: string;
}

function Maintenance({ year, month, line, station, title = "Maintenance" }: MaintenanceProps) {
  // API-driven state
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [scenarioData, setScenarioData] = useState<ScenarioData | null>(null);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(true);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [errorWorkOrders, setErrorWorkOrders] = useState<string | null>(null);
  const [errorScenarios, setErrorScenarios] = useState<string | null>(null);

  // Mode toggle
  const [mode, setMode] = useState<'mock' | 'live'>('mock');

  // Scenario controls
  const [strategy, setStrategy] = useState('Minimize Total Cost');
  const [crewSize, setCrewSize] = useState(2);
  const [fixedEvents, setFixedEvents] = useState(2);
  
  // State for the new section
  const [selectedStation, setSelectedStation] = useState<WorkOrder | null>(null);

  // Define machine names for charts
  const machineNames = ['Plasma Etcher 1', 'Plasma Etcher 2', 'PECVD 1', 'PECVD 2', 'RIE Etcher 1'];

  // Get theme - must be called before any conditional returns
  const theme = useTheme();

  // Load work orders from API
  useEffect(() => {
    async function loadWorkOrders() {
      setLoadingWorkOrders(true);
      setErrorWorkOrders(null);
      try {
        const response = await fetch(`${API_BASE}/api/maintenance/stations?mode=${mode}`);
        const workOrdersData = await response.json();
        // Add mock work orders for selected station
        const workOrdersWithDetails = workOrdersData.map((wo: WorkOrder) => ({
          ...wo,
          workOrders: [
            { id: '1', description: 'Preventive Maintenance', type: 'PM', status: 'Scheduled', date: '2024-01-15' },
            { id: '2', description: 'Calibration Check', type: 'Cal', status: 'Completed', date: '2024-01-10' },
            { id: '3', description: 'Parts Replacement', type: 'Repair', status: 'In Progress', date: '2024-01-12' }
          ]
        }));
        setWorkOrders(workOrdersWithDetails);
        if (workOrdersWithDetails.length > 0) {
          setSelectedStation(workOrdersWithDetails[0]);
        }
      } catch (error) {
        console.error('Error loading work orders:', error);
        setErrorWorkOrders('Error loading work orders');
      } finally {
        setLoadingWorkOrders(false);
      }
    }
    loadWorkOrders();
  }, [mode]);

  // Load scenario data from API
  const runScenario = useCallback(async () => {
    setLoadingScenarios(true);
    setErrorScenarios(null);
    try {
      const response = await fetch(`${API_BASE}/api/maintenance/scenarios?strategy=${strategy}&crewSize=${crewSize}&fixedEvents=${fixedEvents}&mode=${mode}`);
      const scenarios = await response.json();
      
      // Add mock chart data to match original design
      const enhancedScenarios = {
        ...scenarios,
        failureCurves: machineNames.map(() => 
          Array.from({ length: 15 }, (_, i) => ({ day: i + 1, value: Math.random() * 0.8 + 0.1 }))
        ),
        summaryData: [
          { scenario: 'Scenario A', strategy: 'Minimize Cost', maxCrew: 1.5, production: 3200, cost: 1800 },
          { scenario: 'Scenario B', strategy: 'Minimize Downtime', maxCrew: 2.0, production: 3800, cost: 2200 },
          { scenario: 'Scenario C', strategy: 'Balanced', maxCrew: 1.8, production: 3500, cost: 2000 }
        ],
        scheduleData: [
          { machine: 'Plasma Etcher 1', start: 3, end: 5, type: 'Fixed' },
          { machine: 'Plasma Etcher 1', start: 15, end: 17, type: 'Flexible' },
          { machine: 'Plasma Etcher 1', start: 28, end: 30, type: 'Fixed' },
          { machine: 'Plasma Etcher 2', start: 1, end: 3, type: 'Fixed' },
          { machine: 'Plasma Etcher 2', start: 12, end: 14, type: 'Flexible' },
          { machine: 'Plasma Etcher 2', start: 25, end: 27, type: 'Fixed' },
          { machine: 'PECVD 1', start: 5, end: 7, type: 'Fixed' },
          { machine: 'PECVD 1', start: 18, end: 20, type: 'Flexible' },
          { machine: 'PECVD 2', start: 8, end: 10, type: 'Fixed' },
          { machine: 'PECVD 2', start: 22, end: 24, type: 'Flexible' },
          { machine: 'RIE Etcher 1', start: 2, end: 4, type: 'Fixed' },
          { machine: 'RIE Etcher 1', start: 16, end: 18, type: 'Flexible' },
          { machine: 'RIE Etcher 1', start: 29, end: 31, type: 'Fixed' }
        ],
        productionData: Array.from({ length: 15 }, (_, i) => {
          const data: any = { day: i + 1 };
          machineNames.forEach(name => {
            data[name] = Math.floor(Math.random() * 100) + 50;
          });
          return data;
        }),
        crewUsageData: Array.from({ length: 15 }, (_, i) => ({
          day: i + 1,
          people: Math.floor(Math.random() * 3) + 1
        })),
        costData: [
          { label: 'Labor', value: 45000 },
          { label: 'Parts', value: 35000 },
          { label: 'Equipment', value: 20000 },
          { label: 'Overhead', value: 15000 },
          { label: 'Total', value: 115000, bold: true }
        ]
      };
      
      setScenarioData(enhancedScenarios);
    } catch (error) {
      console.error('Error loading scenario data:', error);
      setErrorScenarios('Error loading scenario data');
    } finally {
      setLoadingScenarios(false);
    }
  }, [strategy, crewSize, fixedEvents, mode]);

  // Load initial scenario data
  useEffect(() => {
    runScenario();
  }, [runScenario]);

  // Calculate the percentage for the circular progress
  const totalAssets = (workOrders || []).length;
  const assetsWithRepeatableWorkOrders = (workOrders || []).filter(s => s.repeatableCount > 0).length;
  const repeatablePercentage = totalAssets > 0 ? (assetsWithRepeatableWorkOrders / totalAssets) * 100 : 0;

  // Loading state
  if (loadingWorkOrders) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (errorWorkOrders) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">{errorWorkOrders}</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 4, borderRadius: 0, minHeight: 400, bgcolor: theme.palette.background.default, color: theme.palette.text.primary }}>
      {/* Header with Title, Mode Toggle, and Scenario Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>{title}</Typography>
        
        {/* Mode Toggle */}
        <ButtonGroup variant="outlined" size="small" sx={{ mr: 2 }}>
          <Button
            variant={mode === 'mock' ? 'contained' : 'outlined'}
            onClick={() => setMode('mock')}
          >
            Mock Data
          </Button>
          <Button
            variant={mode === 'live' ? 'contained' : 'outlined'}
            onClick={() => setMode('live')}
          >
            Live Data
          </Button>
        </ButtonGroup>

        {/* Scenario Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Strategy</InputLabel>
            <Select value={strategy} label="Strategy" onChange={(e: SelectChangeEvent) => setStrategy(e.target.value)}>
              <MenuItem value="Minimize Total Cost">Minimize Total Cost</MenuItem>
              <MenuItem value="Minimize Downtime">Minimize Downtime</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Crew size</InputLabel>
            <Select value={crewSize.toString()} label="Crew size" onChange={(e: SelectChangeEvent) => setCrewSize(Number(e.target.value))}>
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Fixed Maintenance</InputLabel>
            <Select value={fixedEvents.toString()} label="Fixed Maintenance" onChange={(e: SelectChangeEvent) => setFixedEvents(Number(e.target.value))}>
              <MenuItem value={1}>1 event</MenuItem>
              <MenuItem value={2}>2 events</MenuItem>
              <MenuItem value={3}>3 events</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" onClick={runScenario}>
            Run Scenario
          </Button>
          <Button variant="outlined" color="primary">
            Deploy Scenario
          </Button>
        </Box>
      </Box>
      
      <Grid container spacing={2}>
        {/* Assets Maintenance Section (Left Column) */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 4, minHeight: 400, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Assets Maintenance</Typography>
              <Button size="small" sx={{ textTransform: 'none' }}>See all</Button>
            </Box>
            <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ borderBottom: '1px solid #eee', py: '12px' }}></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, borderBottom: '1px solid #eee', py: '12px', pr: 2 }}>Repeatable</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(workOrders || []).map((station) => (
                    <TableRow
                      key={station.id}
                      hover
                      onClick={() => setSelectedStation(station)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: selectedStation?.id === station.id ? 'action.hover' : 'transparent',
                        '&:last-child td, &:last-child th': { border: 0 },
                      }}
                    >
                      <TableCell component="th" scope="row" sx={{ borderBottom: '1px solid #eee', py: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={station.image} alt={station.name} sx={{ width: 36, height: 36 }} />
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{station.name}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>{station.location}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#0071e3', borderBottom: '1px solid #eee', py: '8px', pr: 2 }}>{station.repeatableCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Circular Progress and Work Orders (Right Column) */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 4, minHeight: 400, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Circular Progress Gauge */}
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2, mt: 4 }}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={180}
                thickness={2}
                sx={{
                  color: '#e0e0e0',
                  position: 'absolute',
                }}
              />
              <CircularProgress
                variant="determinate"
                value={repeatablePercentage}
                size={180}
                thickness={2}
                sx={{
                  color: '#0071e3',
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h4" component="div" color="text.primary" sx={{ fontWeight: 700 }}>
                  {`${repeatablePercentage.toFixed(1)}%`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Assets with <br /> Repeatable <br /> Work Orders
                </Typography>
              </Box>
            </Box>
            
            {/* Alert for no repeatable work orders */}
            <Stack sx={{ width: '90%', mb: 2 }} spacing={2}>
              <MuiAlert severity="warning" icon={<WarningIcon />}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {totalAssets - assetsWithRepeatableWorkOrders} Assets have no Repeatable Work Orders assigned. <Button size="small" sx={{ textTransform: 'none' }}>See all</Button>
                </Typography>
              </MuiAlert>
            </Stack>
            
            {/* Work Orders Table */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 700, alignSelf: 'flex-start' }}>
              Repeatable Work Orders for {selectedStation?.name}
            </Typography>
            <TableContainer sx={{ flexGrow: 1, width: '100%', maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid #e0e0e0' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid #e0e0e0' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid #e0e0e0' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid #e0e0e0' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedStation?.workOrders || []).length > 0 ? (
                    (selectedStation?.workOrders || []).map((order) => (
                      <TableRow key={order.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ borderBottom: '1px solid #eee' }}>{order.description}</TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #eee' }}>{order.type}</TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #eee' }}>{order.status}</TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #eee' }}>{order.date}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, borderBottom: '1px solid #eee' }}>No repeatable work orders for this station.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Predicted Failure and Summary */}
        <Grid item xs={12} md={7}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
            Predicted Failure
          </Typography>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" type="number" domain={[1, 15]} />
              <YAxis />
              <Tooltip />
              <Legend />
              {scenarioData?.failureCurves?.map((curve, idx) => (
                <Line
                  key={machineNames[idx]}
                  data={curve}
                  dataKey="value"
                  name={machineNames[idx]}
                  stroke={["#0071e3", "#a259ff", "#ff7f7f", "#34c759", "#ffb300"][idx % 5]}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
            Summary
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Strategy</Typography>
            {scenarioData?.summaryData?.map(s => (
              <Box key={s.scenario} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ width: 90 }}>{s.scenario}</Typography>
                <Typography variant="body2">{s.strategy}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Max Crew Usage</Typography>
            <ResponsiveContainer width="100%" height={30}>
              <BarChart data={scenarioData?.summaryData} layout="vertical">
                <XAxis type="number" hide domain={[0, 2]} />
                <YAxis dataKey="scenario" type="category" hide />
                <Bar dataKey="maxCrew" fill="#0071e3" barSize={12} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Total Production</Typography>
            <ResponsiveContainer width="100%" height={30}>
              <BarChart data={scenarioData?.summaryData} layout="vertical">
                <XAxis type="number" hide domain={[0, 5000]} />
                <YAxis dataKey="scenario" type="category" hide />
                <Bar dataKey="production" fill="#34c759" barSize={12} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Total Cost</Typography>
            <ResponsiveContainer width="100%" height={30}>
              <BarChart data={scenarioData?.summaryData} layout="vertical">
                <XAxis type="number" hide domain={[0, 3000]} />
                <YAxis dataKey="scenario" type="category" hide />
                <Bar dataKey="cost" fill="#a259ff" barSize={12} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        {/* Schedule (Gantt-like) */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Predictive Maintenance Schedule - This Month
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#0071e3', borderRadius: 1 }} />
              <Typography variant="body2">Fixed Maintenance</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#a259ff', borderRadius: 1 }} />
              <Typography variant="body2">Flexible Maintenance</Typography>
            </Box>
          </Box>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={scenarioData?.scheduleData} layout="vertical" barCategoryGap={4}>
              <XAxis type="number" domain={[1, 31]} hide />
              <YAxis dataKey="machine" type="category" width={120} />
              <Tooltip 
                formatter={(value: any, name: any, props: any) => [
                  `${props.payload.type} Maintenance`,
                  `Days ${props.payload.start}-${props.payload.end}`
                ]}
                labelFormatter={(label) => `Machine: ${label}`}
              />
              <Bar
                dataKey={() => 1}
                minPointSize={10}
                isAnimationActive={false}
                barSize={20}
                background={false}
                shape={({ x, y, width, height, payload }: any) => {
                  const color = payload.type === 'Fixed' ? '#0071e3' : '#a259ff';
                  return (
                    <rect
                      x={x + (payload.start - 1) * (width / 31)}
                      y={y}
                      width={((payload.end - payload.start + 1) * (width / 31))}
                      height={height}
                      rx={4}
                      fill={color}
                      opacity={0.8}
                    />
                  );
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Grid>

        {/* Production (Stacked Bar) */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Production
          </Typography>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={scenarioData?.productionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              {machineNames.map((m, idx) => (
                <Bar key={m} dataKey={m} stackId="a" fill={["#0071e3", "#a259ff", "#ff7f7f", "#34c759", "#ffb300"][idx % 5]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Grid>

        {/* Crew Usage (Bar) and Cost Table */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Crew Usage
          </Typography>
          <ResponsiveContainer width="100%" height={80}>
            <BarChart data={scenarioData?.crewUsageData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="people" fill="#0071e3" />
            </BarChart>
          </ResponsiveContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Cost
          </Typography>
          <Box sx={{ background: '#fafbfc', borderRadius: 2, p: 2 }}>
            {scenarioData?.costData?.map(row => (
              <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: row.bold ? 700 : 400, fontSize: row.bold ? 18 : 15, mt: row.bold ? 1 : 0 }}>
                <span>{row.label}</span>
                <span>${row.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default Maintenance; 